import {
    Alert,
    Autocomplete,
    Box,
    Button,
    FormHelperText,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    Paper,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import LocationPinIcon from '@mui/icons-material/LocationOn';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
    Add,
    Edit as EditIcon,
    InfoOutlined,
    Search,
} from '@mui/icons-material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { ContractorProfile } from './schema';
import ServiceState from './ServiceState';
import { ServiceAreaRegionSummary } from './ServiceAreaRegionSummary';
import { ProfileShellInlineSectionSubtitle } from '../../ProfileReadOnlyField';
import {
    CONTRACTOR_STATE_CODES,
    CONTRACTOR_STATE_LONG_LABEL,
    type ContractorAuState,
    contractorStateTitle,
} from './contractorStateLabels';

interface ServiceAreasProps {
    isEditing: boolean;
    compactShell?: boolean;
}

export type Contractor = z.infer<typeof ContractorProfile>;

/** Same typography as {@link ProfileShellFormCell} / company shell field labels */
const shellFieldLabelSx = {
    color: 'text.secondary',
    fontWeight: 500,
    letterSpacing: 0.02,
} as const;

function stateMatchesQuery(code: ContractorAuState, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    if (code.toLowerCase().includes(q)) return true;
    const long = String(CONTRACTOR_STATE_LONG_LABEL[code] ?? '').toLowerCase();
    return long.includes(q);
}

const CoverageSummaryPanel = ({
    serviceStates,
    isEditing,
    onEditState,
}: {
    serviceStates: Contractor['serviceStates'] | undefined;
    isEditing: boolean;
    onEditState: (index: number) => void;
}) => {
    const theme = useTheme();
    const rows = serviceStates ?? [];

    return (
        <Stack
            spacing={2}
            sx={{
                minWidth: 0,
                width: '100%',
                display: { xs: 'none', sm: 'flex' },
            }}
        >
            <Box>
                <Typography
                    variant='caption'
                    component='div'
                    sx={shellFieldLabelSx}
                >
                    Coverage summary
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5 }}
                >
                    Review your selected coverage.
                </Typography>
            </Box>

            <Stack spacing={1.5}>
                {rows.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                        No states added yet.
                    </Typography>
                ) : (
                    rows.map((row, index) => {
                        const title = contractorStateTitle(row.state);
                        const hasRegions = (row.areas?.length ?? 0) > 0;
                        const badgeEntire = !hasRegions;
                        const areaNames = row.areas
                            .map((a) => a.name)
                            .filter((n): n is string => Boolean(n));
                        const summaryLine = hasRegions
                            ? areaNames.join(', ') ||
                              'Specific regions selected.'
                            : 'You service the whole state.';

                        return (
                            <Paper
                                key={`${row.state}-${index}`}
                                variant='outlined'
                                sx={{
                                    borderRadius: 2,
                                    p: 1.75,
                                    borderColor: 'divider',
                                }}
                            >
                                <Stack
                                    direction='row'
                                    alignItems='flex-start'
                                    justifyContent='space-between'
                                    spacing={1.5}
                                >
                                    <Stack
                                        direction='row'
                                        spacing={1.5}
                                        alignItems='flex-start'
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                ...(badgeEntire
                                                    ? {
                                                          bgcolor: lighten(
                                                              theme.palette
                                                                  .yellow.main,
                                                              0.82,
                                                          ),
                                                      }
                                                    : {
                                                          bgcolor: alpha(
                                                              theme.palette.text
                                                                  .primary,
                                                              0.06,
                                                          ),
                                                      }),
                                            }}
                                        >
                                            {badgeEntire ? (
                                                <TravelExploreIcon
                                                    sx={{
                                                        fontSize: 22,
                                                        color: theme.palette
                                                            .yellow.main,
                                                    }}
                                                />
                                            ) : (
                                                <LocationPinIcon
                                                    sx={{
                                                        fontSize: 22,
                                                        color: 'text.secondary',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                variant='body2'
                                                fontWeight={700}
                                                color='text.primary'
                                                sx={{ lineHeight: 1.35 }}
                                            >
                                                {title}
                                            </Typography>
                                            <ServiceAreaRegionSummary
                                                text={summaryLine}
                                                areaNames={
                                                    hasRegions
                                                        ? areaNames
                                                        : undefined
                                                }
                                                enableClamp={hasRegions}
                                            />
                                        </Box>
                                    </Stack>
                                    {isEditing ? (
                                        <Tooltip title='Edit coverage'>
                                            <IconButton
                                                type='button'
                                                size='small'
                                                aria-label={`Edit ${row.state}`}
                                                onClick={() =>
                                                    onEditState(index)
                                                }
                                                sx={{
                                                    color: 'text.secondary',
                                                    flexShrink: 0,
                                                    mt: -0.25,
                                                }}
                                            >
                                                <EditIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Stack>
                            </Paper>
                        );
                    })
                )}
            </Stack>
        </Stack>
    );
};

const ServiceAreas = ({
    isEditing,
    compactShell = false,
}: ServiceAreasProps) => {
    const {
        control,
        formState: { errors },
    } = useFormContext<Contractor>();

    const { fields, append, remove } = useFieldArray({
        name: 'serviceStates',
        control,
    });

    const serviceStatesW = useWatch({
        control,
        name: 'serviceStates',
    }) as Contractor['serviceStates'] | undefined;

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedStateToAdd, setSelectedStateToAdd] =
        useState<ContractorAuState | null>(null);
    const [coverageExpandedIndex, setCoverageExpandedIndex] = useState<
        number | null
    >(null);
    const wasEditingCompactRef = useRef(false);

    type State = ContractorAuState;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAddState = (state: State) => {
        const newStateIndex = fields.length;
        append({
            countryCode: 'AU',
            state,
            areas: [],
        });
        if (compactShell) {
            setCoverageExpandedIndex(newStateIndex);
        }
        handleClose();
        setSelectedStateToAdd(null);
    };

    const availableStates = useMemo(
        () =>
            CONTRACTOR_STATE_CODES.filter(
                (state) => !fields.some((field) => field.state === state),
            ),
        [fields],
    );

    useEffect(() => {
        if (
            selectedStateToAdd &&
            !availableStates.includes(selectedStateToAdd)
        ) {
            setSelectedStateToAdd(null);
        }
    }, [availableStates, selectedStateToAdd]);

    const handleRemoveState = (stateIndex: number) => {
        remove(stateIndex);
        setCoverageExpandedIndex((prev) => {
            if (prev === null) return null;
            if (prev === stateIndex) return null;
            if (prev > stateIndex) return prev - 1;
            return prev;
        });
    };

    useEffect(() => {
        const inCompactEdit = isEditing && compactShell;
        if (!inCompactEdit) {
            wasEditingCompactRef.current = false;
            return;
        }
        const justEnteredEdit = !wasEditingCompactRef.current;
        wasEditingCompactRef.current = true;

        if (fields.length === 0) {
            setCoverageExpandedIndex(null);
            return;
        }
        setCoverageExpandedIndex((prev) => {
            if (justEnteredEdit) return null;
            if (prev === null) return null;
            if (prev >= fields.length) return fields.length - 1;
            return prev;
        });
    }, [isEditing, compactShell, fields.length]);

    const listBody = (
        <>
            {isEditing && !compactShell && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button variant='outlined' onClick={handleClick}>
                            Add State
                        </Button>
                    </Box>
                    <Popover
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <List>
                            {availableStates.map((state: State) => (
                                <ListItemButton
                                    key={state}
                                    onClick={() => handleAddState(state)}
                                >
                                    <Typography>{state}</Typography>
                                </ListItemButton>
                            ))}
                        </List>
                    </Popover>
                </>
            )}
            {compactShell && !isEditing ? (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            width: '100%',
                            gap: 1.5,
                            alignItems: 'start',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                sm: 'repeat(2, minmax(0, 1fr))',
                            },
                        }}
                    >
                        {fields.map((field, index) => (
                            <Box
                                key={field.id}
                                sx={{
                                    minWidth: 0,
                                    alignSelf: 'start',
                                    width: '100%',
                                }}
                            >
                                <ServiceState
                                    area={field}
                                    index={index}
                                    isEditing={isEditing}
                                    compactShell={compactShell}
                                    accordionExpanded={false}
                                    onAccordionExpandedChange={undefined}
                                    onRemoveState={handleRemoveState}
                                />
                            </Box>
                        ))}
                    </Box>
                    {errors.serviceStates && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            {errors.serviceStates.message}
                        </FormHelperText>
                    )}
                </>
            ) : (
                <List disablePadding sx={{ width: '100%' }}>
                    {fields.map((field, index) => (
                        <ServiceState
                            key={field.id}
                            area={field}
                            index={index}
                            isEditing={isEditing}
                            compactShell={compactShell}
                            accordionExpanded={
                                isEditing && compactShell
                                    ? coverageExpandedIndex === index
                                    : false
                            }
                            onAccordionExpandedChange={
                                isEditing && compactShell
                                    ? (open) =>
                                          setCoverageExpandedIndex(
                                              open ? index : null,
                                          )
                                    : undefined
                            }
                            onRemoveState={handleRemoveState}
                        />
                    ))}
                    {errors.serviceStates && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            {errors.serviceStates.message}
                        </FormHelperText>
                    )}
                </List>
            )}
        </>
    );

    if (compactShell) {
        const coverageHeader = (
            <Box>
                <Typography
                    variant='caption'
                    component='div'
                    sx={shellFieldLabelSx}
                >
                    Coverage area
                </Typography>
                <ProfileShellInlineSectionSubtitle>
                    Choose the states/territories and regions you service.
                </ProfileShellInlineSectionSubtitle>
            </Box>
        );

        if (!isEditing) {
            return (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 0,
                        border: 'none',
                        boxShadow: 'none',
                        bgcolor: 'transparent',
                        p: 0,
                    }}
                >
                    <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
                        {coverageHeader}
                        {listBody}
                    </Stack>
                </Paper>
            );
        }

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    border: 'none',
                    boxShadow: 'none',
                    bgcolor: 'transparent',
                    p: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                        alignItems: 'start',
                        gap: 3,
                        gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            md: 'minmax(0, 2fr) minmax(0, 1fr)',
                        },
                    }}
                >
                    <Stack spacing={2.5} sx={{ minWidth: 0, width: '100%' }}>
                        {coverageHeader}
                        <>
                            <Box>
                                <Typography
                                    variant='caption'
                                    component='div'
                                    sx={{ ...shellFieldLabelSx, mb: 1.5 }}
                                >
                                    Step 1: Add states or territories
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ sm: 'flex-start' }}
                                >
                                    <Autocomplete<
                                        ContractorAuState,
                                        false,
                                        false,
                                        false
                                    >
                                        size='small'
                                        fullWidth
                                        options={availableStates}
                                        value={selectedStateToAdd}
                                        onChange={(_, value) =>
                                            setSelectedStateToAdd(value)
                                        }
                                        getOptionLabel={(option) =>
                                            contractorStateTitle(option)
                                        }
                                        isOptionEqualToValue={(a, b) => a === b}
                                        filterOptions={(
                                            options,
                                            { inputValue },
                                        ) =>
                                            options.filter((s) =>
                                                stateMatchesQuery(
                                                    s,
                                                    inputValue,
                                                ),
                                            )
                                        }
                                        noOptionsText='No matching states or territories'
                                        disabled={availableStates.length === 0}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder='Search states or territories'
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position='start'>
                                                                <Search
                                                                    fontSize='small'
                                                                    color='action'
                                                                />
                                                            </InputAdornment>
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .startAdornment
                                                            }
                                                        </>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-input::placeholder':
                                                        {
                                                            fontSize: '12px',
                                                            opacity: 1,
                                                        },
                                                }}
                                            />
                                        )}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    <Button
                                        variant='outlined'
                                        startIcon={<Add />}
                                        type='button'
                                        onClick={() => {
                                            if (selectedStateToAdd) {
                                                handleAddState(
                                                    selectedStateToAdd,
                                                );
                                            }
                                        }}
                                        disabled={
                                            !selectedStateToAdd ||
                                            availableStates.length === 0
                                        }
                                        sx={{
                                            flexShrink: 0,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            mt: { xs: 0, sm: 0.25 },
                                        }}
                                    >
                                        Add state
                                    </Button>
                                </Stack>
                            </Box>

                            {fields.length > 0 ? (
                                <Box>
                                    <Typography
                                        variant='caption'
                                        component='div'
                                        sx={{ ...shellFieldLabelSx, mb: 1.5 }}
                                    >
                                        Step 2: Choose coverage for each area
                                    </Typography>
                                </Box>
                            ) : null}
                        </>

                        <Stack spacing={0}>{listBody}</Stack>
                    </Stack>
                    <Box
                        sx={{
                            minWidth: 0,
                            width: '100%',
                            position: { md: 'sticky' },
                            top: { md: 16 },
                            alignSelf: 'start',
                        }}
                    >
                        <CoverageSummaryPanel
                            serviceStates={serviceStatesW}
                            isEditing={isEditing}
                            onEditState={(i) => setCoverageExpandedIndex(i)}
                        />
                    </Box>
                </Box>
            </Paper>
        );
    }

    return (
        <Stack gap={1}>
            <Typography
                sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                    py: '30px',
                }}
            >
                Service Locations
            </Typography>
            {listBody}
        </Stack>
    );
};

export default ServiceAreas;
